import React, { useEffect, useState } from 'react';
import { getVehiclesPublic } from '../services/api';

function getEmbedUrl(url) {
  if (!url) return null;
  const tiktok = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (tiktok) return `https://www.tiktok.com/embed/v2/${tiktok[1]}`;
  if (url.includes('tiktok.com/embed/')) return url;
  return null;
}

const CONFIGS = {
  neuf: {
    badge: 'Véhicules neufs',
    title: 'Ils ont choisi',
    titleHighlight: 'le neuf',
    description: 'Ces clients ont fait confiance à Royal Cars pour l\'achat de leur véhicule neuf. Inspectés, garantis, livrés avec soin — ils ont validé leur choix.',
    filterFn: v => v.statut === 'VENDU' && v.tiktokReviewUrl && v.etat === 'Neuf',
  },
  occasion: {
    badge: 'Véhicules d\'occasion',
    title: 'Ils ont fait',
    titleHighlight: 'le bon choix',
    description: 'Chaque véhicule d\'occasion passe entre nos mains avec soin. Ces clients l\'ont expérimenté et partagent leur expérience avec Royal Cars.',
    filterFn: v => v.statut === 'VENDU' && v.tiktokReviewUrl && v.etat === 'Occasion',
  },
  recent: {
    badge: 'Moins de 3 ans',
    title: 'Récemment vendus,',
    titleHighlight: 'déjà appréciés',
    description: 'Les véhicules récents parlent d\'eux-mêmes. Ces clients ont opté pour un modèle jeune chez Royal Cars — et ils en sont ravis.',
    filterFn: v => {
      if (v.statut !== 'VENDU' || !v.tiktokReviewUrl) return false;
      const match = String(v.dateMiseEnCirculation || '').match(/\d{4}/);
      const annee = match ? parseInt(match[0]) : null;
      return annee !== null && annee >= new Date().getFullYear() - 3;
    },
  },
  all: {
    badge: 'Déjà partis',
    title: 'Ils nous font',
    titleHighlight: 'confiance',
    description: 'Nos clients ont fait confiance à Royal Cars et partagent leur expérience. Découvrez leurs témoignages authentiques, filmés le jour de leur livraison.',
    filterFn: v => v.statut === 'VENDU' && v.tiktokReviewUrl,
  },
};

function AvisClientsCarousel({ filterType = 'all', excludeId = null }) {
  const [reviews, setReviews] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [popup, setPopup] = useState(null);
  const [playing, setPlaying] = useState(false);

  const cfg = CONFIGS[filterType] || CONFIGS.all;

  useEffect(() => {
    getVehiclesPublic()
      .then(res => setReviews(res.data.filter(v => cfg.filterFn(v) && v.id !== excludeId)))
      .catch(() => {});
  }, [filterType, excludeId]);

  useEffect(() => {
    reviews.forEach(v => {
      if (!thumbs[v.tiktokReviewUrl]) {
        fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(v.tiktokReviewUrl)}`)
          .then(r => r.json())
          .then(data => {
            if (data.thumbnail_url) setThumbs(prev => ({ ...prev, [v.tiktokReviewUrl]: data.thumbnail_url }));
          })
          .catch(() => {});
      }
    });
  }, [reviews]);

  if (reviews.length === 0) return null;

  const embedUrl = popup ? getEmbedUrl(popup.url) : null;

  return (
    <section className="bg-white py-14 px-4 border-t border-gray-100">
      <div className="max-w-7xl mx-auto">

        {/* En-tête */}
        <div className="mb-10">
          <span className="inline-block bg-yellow-500 text-black text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-wider mb-3">
            {cfg.badge}
          </span>
          <h2 className="text-black font-extrabold text-3xl md:text-4xl mb-3">
            {cfg.title} <span className="text-yellow-500">{cfg.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl leading-relaxed">{cfg.description}</p>
          <div className="flex gap-1 mt-3">
            {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-lg">★</span>)}
          </div>
        </div>

        {/* Carousel */}
        <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
          {reviews.map(v => {
            const thumb = thumbs[v.tiktokReviewUrl];
            return (
              <div
                key={v.id}
                className="flex-shrink-0 relative rounded-2xl overflow-hidden cursor-pointer group shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                style={{ width: '180px', height: '290px' }}
                onClick={() => { setPopup({ url: v.tiktokReviewUrl, vehicle: v }); setPlaying(false); }}
              >
                {thumb ? (
                  <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex flex-col items-center justify-center gap-3">
                    <svg viewBox="0 0 24 24" className="w-10 h-10 text-white opacity-40" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                    </svg>
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center group-hover:bg-yellow-500 group-hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white font-bold text-xs leading-tight truncate">{v.marque} {v.modele}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-yellow-400 text-xs">★★★★★</span>
                    <span className="bg-black/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded border border-white/20">TikTok</span>
                  </div>
                </div>

                <div className="absolute top-3 left-3">
                  <span className="text-black font-extrabold px-2 py-0.5 rounded-lg text-[9px]"
                    style={{ background: 'rgba(234,179,8,0.55)' }}>VENDU</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Popup */}
      {popup && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
          onClick={() => { setPopup(null); setPlaying(false); }}
        >
          <div className="relative flex flex-col items-center w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { setPopup(null); setPlaying(false); }}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition"
            >×</button>

            <p className="text-white font-bold mb-3 text-center">{popup.vehicle.marque} {popup.vehicle.modele}</p>

            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900" style={{ aspectRatio: '9/16', maxHeight: '75vh' }}>
              {playing ? (
                <iframe src={embedUrl} title="Avis client"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen className="w-full h-full" />
              ) : (
                <>
                  {thumbs[popup.url] ? (
                    <img src={thumbs[popup.url]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-black">
                      <svg viewBox="0 0 24 24" className="w-20 h-20 text-white opacity-40" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                      </svg>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center cursor-pointer" onClick={() => setPlaying(true)}>
                    <div className="w-20 h-20 rounded-full bg-white/20 border-2 border-white/80 flex items-center justify-center hover:bg-yellow-500 hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AvisClientsCarousel;
